import { useEffect } from 'react';
import { useLocation } from 'wouter';

interface SEOConfig {
  title: string;
  description: string;
  canonical: string;
  hreflang: Array<{
    lang: string;
    url: string;
  }>;
  ogImage?: string;
  keywords?: string;
  schema?: any;
}

// Base Organization Schema for Dong Hoa AI Data Generation
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Dong Hoa AI Data Generation",
  "description": "Translation service specializing in AI-powered video subtitle generation and data processing",
  "url": "https://subgen.net/",
  "logo": "https://subgen.net/logo.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "12 Đông Hòa 6, Thôn Đông Hòa, xã Hòa Châu",
    "addressLocality": "Đà Nẵng",
    "addressRegion": "Da Nang City",
    "postalCode": "55000",
    "addressCountry": "VN"
  },
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+84-911-455-879",
      "contactType": "customer service",
      "availableLanguage": ["Vietnamese", "English"]
    },
    {
      "@type": "ContactPoint",
      "email": "admin@subgen.net",
      "contactType": "customer service",
      "availableLanguage": ["Vietnamese", "English"]
    }
  ],
  "openingHours": "Mo-Su 00:00-23:59",
  "serviceType": "Translation service",
  "areaServed": "Worldwide"
};

const DEFAULT_SEO: SEOConfig = {
  title: 'Video to Subtitle Generator - Create SRT Subtitles from Video Online',
  description: 'Free online tool to generate subtitles and captions from video files. Convert video to SRT format with AI-powered transcription. Support multiple languages and formats.',
  canonical: 'https://subgen.net/',
  hreflang: [
    { lang: 'en', url: 'https://subgen.net/' },
    { lang: 'de', url: 'https://subgen.net/de/' },
    { lang: 'fr', url: 'https://subgen.net/fr/' },
    { lang: 'vi', url: 'https://subgen.net/vi/' },
    { lang: 'es', url: 'https://subgen.net/es/' },
    { lang: 'x-default', url: 'https://subgen.net/' }
  ],
  ogImage: 'https://subgen.net/og-image.jpg',
  keywords: 'video to subtitle, video subtitle generator, create subtitles from video, video to srt, subtitle generator, video caption generator, srt from video, video transcription, auto subtitle, video to text',
  schema: {
    ...ORGANIZATION_SCHEMA,
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AI Translation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Video to Subtitle Generation",
            "description": "AI-powered video transcription and subtitle generation",
            "url": "https://subgen.net/features"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Translation Services",
            "description": "Professional translation services for multiple languages",
            "url": "https://subgen.net/features"
          }
        }
      ]
    }
  }
};

const PAGE_SEO: Record<string, SEOConfig> = {
  '/': DEFAULT_SEO,
  '/features': {
    title: 'Features - Video to Subtitle Generator',
    description: 'Discover powerful features of our video subtitle generator. AI-powered transcription, multiple language support, SRT format, and more.',
    canonical: 'https://subgen.net/features',
    hreflang: [
      { lang: 'en', url: 'https://subgen.net/features' },
      { lang: 'de', url: 'https://subgen.net/de/features' },
      { lang: 'fr', url: 'https://subgen.net/fr/features' },
      { lang: 'vi', url: 'https://subgen.net/vi/features' },
      { lang: 'es', url: 'https://subgen.net/es/features' },
      { lang: 'x-default', url: 'https://subgen.net/features' }
    ],
    ogImage: 'https://subgen.net/og-image.jpg',
    keywords: 'subtitle generator features, video transcription features, AI subtitle features, SRT generator features',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Video to Subtitle Generation Features",
      "description": "AI-powered video transcription and subtitle generation with advanced features",
      "provider": ORGANIZATION_SCHEMA,
      "serviceType": "Video Transcription",
      "areaServed": "Worldwide",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "AI Translation Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Video to Subtitle Generation",
              "description": "AI-powered video transcription and subtitle generation",
              "url": "https://subgen.net/features"
            }
          }
        ]
      }
    }
  },
  '/pricing': {
    title: 'Pricing - Video to Subtitle Generator',
    description: 'Pay-per-minute pricing for video subtitle generation. Free preview processing, then $0.045 per minute + $0.85 fixed fee for full video processing.',
    canonical: 'https://subgen.net/pricing',
    hreflang: [
      { lang: 'en', url: 'https://subgen.net/pricing' },
      { lang: 'de', url: 'https://subgen.net/de/pricing' },
      { lang: 'fr', url: 'https://subgen.net/fr/pricing' },
      { lang: 'vi', url: 'https://subgen.net/vi/pricing' },
      { lang: 'es', url: 'https://subgen.net/es/pricing' },
      { lang: 'x-default', url: 'https://subgen.net/pricing' }
    ],
    ogImage: 'https://subgen.net/og-image.jpg',
    keywords: 'subtitle generator pricing, video transcription cost per minute, SRT generator pricing, pay per minute subtitle service',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Video to Subtitle Generation Pricing",
      "description": "Pay-per-minute pricing for AI-powered video subtitle generation services",
      "provider": ORGANIZATION_SCHEMA,
      "offers": [
        {
          "@type": "Offer",
          "name": "Free Preview Processing",
          "description": "Free preview processing for all video files",
          "price": "0",
          "priceCurrency": "USD",
          "category": "Preview Service"
        },
        {
          "@type": "Offer",
          "name": "Full Video Processing",
          "description": "Full video subtitle generation at $0.045 per minute + $0.85 fixed fee",
          "price": "0.045",
          "priceCurrency": "USD",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "0.045",
            "priceCurrency": "USD",
            "unitText": "per minute",
            "additionalProperty": {
              "name": "Fixed Fee",
              "value": "0.85"
            }
          },
          "category": "Full Processing Service"
        }
      ]
    }
  },
  '/about': {
    title: 'About - Video to Subtitle Generator',
    description: 'Learn about our mission to provide high-quality video subtitle generation. AI-powered technology for accurate transcription.',
    canonical: 'https://subgen.net/about',
    hreflang: [
      { lang: 'en', url: 'https://subgen.net/about' },
      { lang: 'de', url: 'https://subgen.net/de/about' },
      { lang: 'fr', url: 'https://subgen.net/fr/about' },
      { lang: 'vi', url: 'https://subgen.net/vi/about' },
      { lang: 'es', url: 'https://subgen.net/es/about' },
      { lang: 'x-default', url: 'https://subgen.net/about' }
    ],
    ogImage: 'https://subgen.net/og-image.jpg',
    keywords: 'about subtitle generator, video transcription company, AI subtitle technology, subtitle service mission',
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Dong Hoa AI Data Generation",
      "description": "Leading provider of AI-powered video subtitle generation and translation services",
      "url": "https://subgen.net/",
      "foundingDate": "2024",
      "mission": "To provide high-quality, AI-powered video subtitle generation accessible to everyone",
      "address": ORGANIZATION_SCHEMA.address,
      "contactPoint": ORGANIZATION_SCHEMA.contactPoint,
      "openingHours": ORGANIZATION_SCHEMA.openingHours,
      "serviceType": ORGANIZATION_SCHEMA.serviceType
    }
  },
  '/support': {
    title: 'Support - Video to Subtitle Generator',
    description: 'Get help and support for our video subtitle generator. Contact us for technical assistance and feature requests.',
    canonical: 'https://subgen.net/support',
    hreflang: [
      { lang: 'en', url: 'https://subgen.net/support' },
      { lang: 'de', url: 'https://subgen.net/de/support' },
      { lang: 'fr', url: 'https://subgen.net/fr/support' },
      { lang: 'vi', url: 'https://subgen.net/vi/support' },
      { lang: 'es', url: 'https://subgen.net/es/support' },
      { lang: 'x-default', url: 'https://subgen.net/support' }
    ],
    ogImage: 'https://subgen.net/og-image.jpg',
    keywords: 'subtitle generator support, video transcription help, technical support, customer service',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Customer Support",
      "description": "Professional customer support for video subtitle generation services",
      "provider": ORGANIZATION_SCHEMA,
      "serviceType": "Customer Support",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+84-911-455-879",
        "contactType": "customer service",
        "availableLanguage": ["Vietnamese", "English"],
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "00:00",
          "closes": "23:59"
        }
      }
    }
  }
};

export const useSEO = (pagePath: string = '/') => {
  const [location] = useLocation();
  
  useEffect(() => {
    // Get SEO config for current page
    const seoConfig = PAGE_SEO[pagePath] || DEFAULT_SEO;
    
    // Update document title
    document.title = seoConfig.title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seoConfig.description);
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', seoConfig.keywords || '');
    
    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', seoConfig.canonical);
    
    // Update hreflang tags
    seoConfig.hreflang.forEach(({ lang, url }) => {
      let hreflangLink = document.querySelector(`link[hreflang="${lang}"]`);
      if (!hreflangLink) {
        hreflangLink = document.createElement('link');
        hreflangLink.setAttribute('rel', 'alternate');
        hreflangLink.setAttribute('hreflang', lang);
        document.head.appendChild(hreflangLink);
      }
      hreflangLink.setAttribute('href', url);
    });
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seoConfig.title);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seoConfig.description);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', seoConfig.canonical);
    }
    
    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', seoConfig.title);
    }
    
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', seoConfig.description);
    }
    
    let twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) {
      twitterUrl.setAttribute('content', seoConfig.canonical);
    }
    
    // Add/Update structured data schema
    if (seoConfig.schema) {
      let existingSchema = document.querySelector('script[type="application/ld+json"]');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      const schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.textContent = JSON.stringify(seoConfig.schema, null, 2);
      document.head.appendChild(schemaScript);
    }
    
  }, [location, pagePath]);
  
  return PAGE_SEO[pagePath] || DEFAULT_SEO;
};
